import * as api from '../../../api';
import { ERROR_VAULT_SOLE_OWNER } from '../../../ApiError';

import {
    handleDisableUser2FA,
    handlePrimaryAccount,
    usersFetchers,
} from '../../Services';
import { isEqual, sortBy } from 'lodash';

import Access from 'ui/Access';
import React from 'react';
import { SUBSCRIPTION_MESSAGES } from '../../subscription/utils/consts';
import { TAB_FORM_LIST } from '../utils/consts';
import UsersView from '../../shared/users/UsersView';
import { flowRight } from 'lodash/fp';
import { isOrganizationAdmin } from '../../../utils/user';
import { userActions } from '../../shared/users/types';
import { withAppContext } from '../../AppContext';
import { withCompanyContext } from '../CompanyContext';
import { withUsers } from '../../hooks/withUsers';

class Users extends React.Component {
    state = {
        users: [],
        usersStats: {},
        usersShowActionResult: false,
        userLoading: true,
        userValidating: true,
        currentUser: null,
        currentGroup: null,
        groups: [],
        formErrors: null,
    };

    componentDidMount() {
        const id = this.getCompanyId();
        this.fetchGroups(id);
    }

    componentDidUpdate() {
        const { data, error, isLoading, isValidating } = this.props;
        const isEqualUsers = isEqual(
            sortBy(data?.users, 'uuid'),
            sortBy(this.state.users, 'uuid')
        );
        const isEqualUsersStats = isEqual(data?.stats, this.state.usersStats);

        if (
            this.state.userLoading !== isLoading ||
            !isEqualUsers ||
            this.state.userValidating !== isValidating ||
            !isEqualUsersStats
        ) {
            const updates = {};

            if (
                !isLoading &&
                (this.state.userLoading || !isEqualUsers || !isEqualUsersStats)
            ) {
                updates.users = data.users;
                updates.usersStats = data.stats;
                updates.userLoading = false;
                updates.usersShowActionResult = false;
            }

            if (!isValidating && this.state.userValidating) {
                updates.userValidating = false;
            }

            if (Object.keys(updates).length) {
                this.setState(updates);
            }
        }
    }

    showMessage = (msg, delay = null) => {
        let options = {
            variant: 'info',
        };
        if (delay) {
            options['autoHideDuration'] = delay;
        }
        this.props.onMessage(msg, options);
    };

    handleDeleteUsers = selectedUsers => {
        const data = selectedUsers.reduce(
            (acc, { uuid, item_type }) => {
                if (item_type === 'account') {
                    acc.accounts.push({ uuid });
                } else {
                    acc.invitations.push({ uuid });
                }
                return acc;
            },
            { accounts: [], invitations: [] }
        );
        const id = this.getCompanyId();
        this.setState({ userLoading: true }, () => {
            usersFetchers(
                this.props.app.currentUser.uuid,
                id,
                data,
                'REMOVE',
                api.remove(`/api/organization/${id}/users`, data)
            ).then(response => {
                if (response.error) {
                    if (response.id === ERROR_VAULT_SOLE_OWNER) {
                        this.showMessage(response.error, 8000);
                    } else {
                        this.showMessage(SUBSCRIPTION_MESSAGES.DeleteFail);
                    }
                } else {
                    this.showMessage(SUBSCRIPTION_MESSAGES.Delete);
                }
            });
        });
    };

    handleDisableUsers = selectedUsers => {
        const data = selectedUsers.map(({ uuid }) => ({
            uuid,
            disabled: true,
        }));
        this.showMessage(SUBSCRIPTION_MESSAGES.Disable);
        this.patchUsersForCompany(this.getCompanyId(), { accounts: data });
    };

    handleTransferPrimaryAccount = selectedUsers => {
        this.showMessage('Transferring primary user account.');
        this.patchPrimaryAccount(this.getCompanyId(), {
            primary_account: selectedUsers[0].uuid,
        });
    };

    handleEnabledUsers = selectedUsers => {
        const data = selectedUsers.map(({ uuid }) => ({
            uuid,
            enabled: true,
        }));
        this.showMessage(SUBSCRIPTION_MESSAGES.Enable);
        this.patchUsersForCompany(this.getCompanyId(), { accounts: data });
    };

    handleApprovePendingUsers = selectedUsers => {
        const data = selectedUsers.map(({ uuid }) => ({
            uuid,
            status: 'creating_account',
        }));
        this.showMessage('User(s) will be approve.');
        this.patchUsersForCompany(this.getCompanyId(), { invitations: data });
    };

    handleConvertToPersonalAccount = selectedUsers => {
        const data = selectedUsers.reduce(
            (acc, { organization_account_uuid }) => {
                acc.accounts.push({ uuid: organization_account_uuid });
                return acc;
            },
            { accounts: [] }
        );
        const id = this.getCompanyId();
        this.setState({ userLoading: true }, () => {
            usersFetchers(
                this.props.app.currentUser.uuid,
                id,
                data,
                'REMOVE',
                api.remove(`/api/organization/${id}/accounts`, data)
            );
        });
    };

    handleDisableUser2FA = user => {
        this.setState({ userLoading: true }, () => {
            const id = this.getCompanyId();
            const usersUrl = `/api/organization/${id}/users`;
            const cacheKey = this.props.app.currentUser.uuid + '-' + usersUrl;
            const data = {
                uuid: user.uuid,
            };
            this.showMessage('Disable user 2FA.');
            handleDisableUser2FA(
                cacheKey,
                id,
                data,
                api.post(`/api/organization/${id}/disable_user_2fa`, data)
            );
        });
    };

    handleRequireUsersPasswordChange = selectedUsers => {
        const data = selectedUsers.map(({ uuid }) => ({
            uuid,
            master_password_change_required: true,
        }));
        this.showMessage(SUBSCRIPTION_MESSAGES.ChangePassword, 5000);
        this.patchUsersForCompany(this.getCompanyId(), { accounts: data });
    };

    handleResetUsersPassword = (
        selectedUsers,
        usersShowActionResult,
        option = {},
        callback
    ) => {
        const data = {
            accounts: selectedUsers.map(({ uuid }) => ({
                uuid,
            })),
            ...option,
        };
        const id = this.getCompanyId();
        return new Promise(async (resolve, reject) => {
            this.setState({ userLoading: true });

            try {
                const response = await usersFetchers(
                    this.props.app.currentUser.uuid,
                    id,
                    data,
                    'POST',
                    api.post(`/api/organization/${id}/restart_account`, data)
                );

                if (response.error || !response.status) {
                    this.setState({ userLoading: false });
                    return reject({
                        code: response.id,
                        message: response.message,
                    });
                }

                this.setState({ usersShowActionResult, userLoading: false });
                callback && callback();
                resolve();
            } catch (error) {
                this.setState({ userLoading: false });
                reject(error);
            }
        });
    };

    handleManageUsersInPasswordBoss = selectedUsers => {
        const data = selectedUsers.reduce(
            (acc, { uuid, item_type }) => {
                if (item_type === 'account') {
                    acc.accounts.push({ uuid, managed_by: 'password-boss' });
                } else {
                    acc.invitations.push({ uuid, managed_by: 'password-boss' });
                }
                return acc;
            },
            { accounts: [], invitations: [] }
        );
        const id = this.getCompanyId();
        this.setState({ userLoading: true }, () => {
            usersFetchers(
                this.props.app.currentUser.uuid,
                id,
                data,
                'PATCH',
                api.patch(`/api/organization/${id}/users`, data)
            );
        });
    };

    showMigrationAccountMessage = invitations => {
        const migratingAccountsEmails = invitations
            .filter(inv => Boolean(inv.accepting_account))
            .map(inv => inv.email)
            .join(', ');

        if (migratingAccountsEmails) {
            const msg = `${SUBSCRIPTION_MESSAGES.Migrate} ${migratingAccountsEmails}`;
            this.showMessage(msg, 5000);
        }
    };

    showAddUsersMessage = data => {
        this.showMessage(SUBSCRIPTION_MESSAGES.Create);
        this.showMigrationAccountMessage(data);
    };

    handleAddUser = usersData => {
        const id = this.getCompanyId();
        const currentUserUuid = this.props.app.currentUser.uuid;
        return usersFetchers(
            currentUserUuid,
            id,
            usersData,
            'POST',
            api.post(`/api/organization/${id}/users`, usersData)
        ).then(json => {
            if (json?.error) {
                this.setState({ formErrors: json.error });
            } else {
                this.setState({ formErrors: null });
                this.showAddUsersMessage(json);
            }
        });
    };

    handleAddUsers = usersData => {
        const id = this.getCompanyId();
        const currentUserUuid = this.props.app.currentUser.uuid;
        return usersFetchers(
            currentUserUuid,
            id,
            usersData,
            'POST',
            api.post(`/api/organization/${id}/users`, usersData)
        ).then(json => {
            if (json?.error) {
                this.setState({ formErrors: json.error });
            } else {
                this.setState({ formErrors: null });
                this.showAddUsersMessage(json);
            }
        });
    };

    handleFetchUser = ({ uuid, item_type }) => {
        const id = this.getCompanyId();

        const data = {
            [`${item_type}_uuid`]: uuid,
        };

        api.get(`/api/organization/${id}/users`, data).then(json => {
            const [user] = json.users;
            this.setState({
                currentUser: user,
            });
        });
    };

    handleUpdateUser = userData => {
        const id = this.getCompanyId();
        const { app } = this.props;

        const data = {
            accounts: [userData],
        };
        const shouldUpdateUserData =
            this.props.app.currentUser.uuid === userData.uuid;

        return usersFetchers(
            this.props.app.currentUser.uuid,
            id,
            data,
            'PATCH',
            api.patch(`/api/organization/${id}/users`, data)
        ).then(json => {
            if (json.error) {
                let errorMessage = json.error.split(':');
                if (errorMessage.length === 2) {
                    errorMessage = errorMessage[1].trim();
                } else {
                    errorMessage = errorMessage[0];
                }

                this.setState({
                    formErrors: errorMessage,
                });
            } else {
                this.setState({
                    formErrors: null,
                });
                this.showMessage(SUBSCRIPTION_MESSAGES.Update);
                this.setState({ userLoading: true, currentUser: null }, () => {
                    if (shouldUpdateUserData) {
                        app.updateUserData();
                    }
                });
            }
        });
    };

    handleFormCancel = () => {
        this.setState({
            formErrors: null,
            currentUser: null,
            currentGroup: null,
        });
    };

    fetchUsersForCompany(id, usersShowActionResult = false) {
        api.get(`/api/organization/${id}/users`)
            .then(json => {
                const { users, stats } = json;
                this.setState({
                    users,
                    usersStats: stats,
                    userLoading: false,
                    usersShowActionResult,
                });
            })
            .catch(() => {
                this.setState({
                    userLoading: false,
                });
            });
    }

    patchUsersForCompany(id, data, usersShowActionResult = false) {
        this.setState({ userLoading: true }, async () => {
            const response = await usersFetchers(
                this.props.app.currentUser.uuid,
                id,
                data,
                'PATCH',
                api.patch(`/api/organization/${id}/users`, data)
            );
            if (response.data && response.data.invitations) {
                this.showMigrationAccountMessage(response.data.invitations);
            }

            this.setState({ usersShowActionResult });
        });
    }

    patchPrimaryAccount(id, data, usersShowActionResult = false) {
        this.setState({ userLoading: true }, () => {
            handlePrimaryAccount(
                this.props.app.currentUser.uuid,
                id,
                data,
                api.post(`/api/organization/${id}/primary_user`, data)
            ).then(() => {
                this.setState({ usersShowActionResult });
            });
        });
    }

    fetchGroups(id) {
        if (this.props.groupsAccess) {
            const url = `/api/organization/${id}/groups`;
            api.get(url).then(({ data: groups }) => {
                this.setState({ groups });
            });
        }
    }

    getCompanyId() {
        return this.props.company.id;
    }

    getActions = () => {
        const { app } = this.props;
        const isExternalActiveUser = user =>
            user.managed_by !== 'PB' && user.status === 'active';
        return [
            {
                key: userActions.APPROVE_PENDING,
                name: 'Approve account',
                notAllowedUsersStatuses: [
                    'active',
                    'invited',
                    'disabled',
                    'unmanaged',
                ],
            },
            {
                key: userActions.CONVERT_TO_PERSONAL,
                name: 'Convert to personal account',
                notAllowedUsersStatuses: [
                    'disabled',
                    'invited',
                    'unmanaged',
                    'pending_approval',
                    'Active Directory',
                ],
                actionFilter(user) {
                    return !user.is_primary_account;
                },
            },
            {
                key: userActions.DELETE_USERS,
                name: 'Delete users',
                notAllowedUsersStatuses: ['unmanaged', 'pending_approval'],
                actionFilter(user) {
                    return (
                        !isExternalActiveUser(user) && !user.is_primary_account
                    );
                },
            },
            {
                key: userActions.DISABLE_USERS,
                name: 'Disable users',
                notAllowedUsersStatuses: [
                    'disabled',
                    'invited',
                    'unmanaged',
                    'pending_approval',
                ],
                actionFilter(user) {
                    return user.uuid !== app.currentUser.uuid;
                },
            },
            {
                key: userActions.ENABLE_USERS,
                name: 'Enable users',
                notAllowedUsersStatuses: [
                    'active',
                    'invited',
                    'unmanaged',
                    'pending_approval',
                ],
            },
            {
                key: userActions.MANAGE_IN_PASSWORDBOSS,
                name: 'Manage in CyberFOX Password Manager',
                notAllowedUsersStatuses: ['managed'],
            },
            {
                key: userActions.REQUIRE_PASSWORD_CHANGE,
                name: 'Require password change',
                notAllowedUsersStatuses: [
                    'disabled',
                    'invited',
                    'unmanaged',
                    'pending_approval',
                ],
            },
            {
                key: userActions.RESET_PASSWORD,
                name: 'Reset master password',
                notAllowedUsersStatuses: [
                    'disabled',
                    'invited',
                    'unmanaged',
                    'pending_approval',
                ],
            },
            {
                key: userActions.TRANSFER_PRIMARY_ACCOUNT,
                name: 'Transfer primary user account role',
                single: true,
                actionFilter: selectedUser =>
                    !selectedUser.is_primary_account &&
                    isOrganizationAdmin(this.props.app.currentUser),
                notAllowedUsersStatuses: [
                    'disabled',
                    'invited',
                    'unmanaged',
                    'pending_approval',
                ],
            },
        ];
    };

    handleResetForm = () => {
        this.setState({ formErrors: null });
    };

    validDeleteUsers(selected) {
        const { currentUser } = this.app;

        if (currentUser && selected.some(u => u.uuid === currentUser.uuid)) {
            return userActions.DELETE_YOURSELF_INVALID;
        }
        const admins = this.users.filter(a => a.role === 'Administrator');
        const selectedAdmins = selected.filter(a => a.role === 'Administrator');
        if (selectedAdmins.length >= admins.length) {
            return userActions.DELETE_ADMINS_INVALID;
        }
        return userActions.DELETE_USERS;
    }

    render() {
        const { usersStats } = this.state;
        const actions = this.getActions();
        const stats = [
            { label: 'Active Users', value: usersStats.account_count },
            { label: 'Available Licenses', value: usersStats.remaining_seats },
            {
                label: 'Pending Approval',
                value: usersStats.pending_approval_count,
            },
        ];
        const permissions = {
            users: Access.COMPANIES_USERS,
            groups: Access.COMPANIES_GROUPS,
        };

        return (
            <UsersView
                users={this.state.users}
                stats={stats}
                groups={this.state.groups}
                userLoading={this.state.userLoading}
                currentUser={this.state.currentUser}
                formErrors={this.state.formErrors}
                onReset={this.handleResetForm}
                showActionResult={this.state.usersShowActionResult}
                onActionResult={() =>
                    this.setState({
                        usersShowActionResult: false,
                    })
                }
                onDisableUsers={this.handleDisableUsers}
                onTransferPrimaryAccount={this.handleTransferPrimaryAccount}
                onDeleteUsers={this.handleDeleteUsers}
                onEnableUsers={this.handleEnabledUsers}
                onApprovePendingUsers={this.handleApprovePendingUsers}
                onConvertToPersonalAccount={this.handleConvertToPersonalAccount}
                onResetUsersPassword={this.handleResetUsersPassword}
                onAddUser={this.handleAddUser}
                onAddUsers={this.handleAddUsers}
                onUpdateUser={this.handleUpdateUser}
                onFormCancel={this.handleFormCancel}
                onFetchUser={this.handleFetchUser}
                onRequireUsersPasswordChange={
                    this.handleRequireUsersPasswordChange
                }
                onManageUsersInPasswordBoss={
                    this.handleManageUsersInPasswordBoss
                }
                onDisableUser2FA={this.handleDisableUser2FA}
                editable={this.props.editable}
                actions={actions}
                permissions={permissions}
                tabs={TAB_FORM_LIST}
                validDeleteUsers={this.validDeleteUsers}
                loggedInUser={this.props.app.currentUser}
            />
        );
    }
}

export default flowRight(withAppContext, withCompanyContext, withUsers)(Users);
